import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faLockOpen, faDownload } from '@fortawesome/pro-solid-svg-icons';

import { getMerchantsV2, getMerchantSnapshotUploads, addMerchantTestCase, updateMerchantTestCase } from '../../api/merchants';
import SearchInput from '../SearchInput/SearchInput';
import MerchantDetail from './MerchantDetail';
import PartnerBadge from './PartnerBadge';
import { SOURCE_LABELS, SNAPSHOT_SOURCES, getSourceLabel, getSourceTagClass, formatMerchantRate } from './merchantSourceHelpers';
import './MerchantsV2.scss';

const SOURCE_OPTIONS = Object.entries({ ...SOURCE_LABELS, other: 'Other' }).map(([value, label]) => ({
  value,
  label: <span className={cn('network-tag', getSourceTagClass(value))}>{label}</span>
}));

const FLAG_OPTIONS = [
  { value: 'locked', label: 'Locked' },
  { value: 'no_test_case', label: 'No test case' },
  { value: 'multi_network', label: 'Multiple networks' },
  { value: 'closed_program', label: 'Closed program' },
  { value: 'increased_rate', label: 'Increased rate' }
];

const SORT_OPTIONS = [
  { value: 'popular', label: 'Most popular' },
  { value: 'name_asc', label: 'Name A→Z' },
  { value: 'rate_desc', label: 'Highest rate' },
  { value: 'updated_desc', label: 'Recently updated' }
];

const MerchantsV2 = () => {
  const { id } = useParams();
  return id ? <MerchantDetail id={id} /> : <MerchantDirectory />;
};

const MerchantDirectory = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [sources, setSources] = useState([]);
  const [filters, setFilters] = useState([]);
  const [brandFilter, setBrandFilter] = useState(null);
  const [brandLens, setBrandLens] = useState(null);
  const [sort, setSort] = useState('popular');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, 250);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setError(null);
    getMerchantsV2({
      query,
      source: sources.join(','),
      filters: filters.join(','),
      Brand_id: brandFilter,
      sort,
      page
    })
      .then(result => {
        if (!isActive) return;
        setRows(result.merchants);
        setHasMore(!!result.hasMore);
        setPageSize(result.pageSize);
        setBrandLens(result.brand || null);
      })
      .catch(requestError => isActive && setError(requestError || 'Could not load merchants.'))
      .finally(() => isActive && setIsLoading(false));
    return () => {
      isActive = false;
    };
  }, [query, sources, filters, brandFilter, sort, page]);

  const toggleFilter = filterKey => {
    setFilters(filters.includes(filterKey) ? filters.filter(key => key !== filterKey) : [...filters, filterKey]);
    setPage(0);
  };

  const toggleSource = sourceKey => {
    setSources(sources.includes(sourceKey) ? sources.filter(key => key !== sourceKey) : [...sources, sourceKey]);
    setPage(0);
  };

  const onBrandClick = brandId => {
    setBrandFilter(prev => (prev === brandId ? null : brandId));
    setPage(0);
  };

  // Locks live on the domain's test case, so every network row on that domain shares them.
  const onRowChange = updated =>
    setRows(prev => prev.map(row => (row.domain === updated.domain ? { ...row, testCase: updated.testCase, locks: updated.locks } : row)));

  const firstRowNumber = page * pageSize + 1;
  const lastRowNumber = page * pageSize + rows.length;

  return (
    <div className='merchants-v2-page'>
      <div className='merchants-v2-tools'>
        <SearchInput
          className='merchants-v2-search'
          value={search}
          onChange={event => setSearch(event.target.value)}
          onClear={() => setSearch('')}
          placeholder='Search by merchant name, domain, or network ID...'
          isLoading={isLoading}
          showSearchIcon
        />
        <div className='merchants-v2-filter-row'>
          <div className='merchants-v2-filter-chips'>
            <CheckboxDropdown
              label='Networks'
              options={SOURCE_OPTIONS}
              selected={sources}
              onToggle={toggleSource}
              onClear={() => {
                setSources([]);
                setPage(0);
              }}
            />
            <CheckboxDropdown
              label='Flags'
              options={FLAG_OPTIONS}
              selected={filters.filter(key => key !== 'brand_partner')}
              onToggle={toggleFilter}
              onClear={() => {
                setFilters(filters.filter(key => key === 'brand_partner'));
                setPage(0);
              }}
            />
            <button
              className={cn('merchants-v2-chip', 'partners-chip', { active: filters.includes('brand_partner') })}
              onClick={() => toggleFilter('brand_partner')}
            >
              <PartnerBadge size={13} /> Partners only
            </button>
          </div>
          <div className='merchants-v2-filter-actions'>
            <SnapshotDownloadsDropdown />
            <select
              className='merchants-v2-select'
              value={sort}
              onChange={event => {
                setSort(event.target.value);
                setPage(0);
              }}
            >
              {SORT_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && <div className='merchants-v2-error'>{String(error)}</div>}

      {brandFilter && brandLens && (
        <div className='merchants-v2-brand-lens'>
          <PartnerBadge size={20} />
          <div className='brand-lens-info'>
            <div className='brand-lens-name'>{brandLens.name}</div>
            <div className='brand-lens-meta'>
              Brand partner · {brandLens.domains.length} {brandLens.domains.length === 1 ? 'domain' : 'domains'} · {brandLens.merchantCount}{' '}
              {brandLens.merchantCount === 1 ? 'network row' : 'network rows'} · {brandLens.liveCount} live
            </div>
            <div className='brand-lens-domains'>{brandLens.domains.join(', ')}</div>
          </div>
          <button
            className='brand-lens-clear'
            onClick={() => {
              setBrandFilter(null);
              setPage(0);
            }}
          >
            Clear
          </button>
        </div>
      )}

      <div className='merchants-v2-table'>
        <div className='merchants-v2-table-header'>
          <div className='col-name'>Merchant</div>
          <div className='col-domain'>Domain</div>
          <div className='col-network'>Network</div>
          <div className='col-rate'>Rate</div>
          <div className='col-live'>Live</div>
          <div className='col-note'>Note</div>
          <div className='col-updated'>Updated</div>
        </div>
        {isLoading && !rows.length ? (
          <div className='merchants-v2-empty'>Loading merchants...</div>
        ) : !rows.length ? (
          <div className='merchants-v2-empty'>No merchants match this search. Try a different name, domain, or filter.</div>
        ) : (
          rows.map(row => <MerchantRow key={row.id} row={row} onBrandClick={onBrandClick} onRowChange={onRowChange} />)
        )}
      </div>

      <div className='merchants-v2-pagination'>
        <button disabled={page === 0} onClick={() => setPage(page - 1)}>
          Previous
        </button>
        <span>
          Page {page + 1}
          {rows.length > 0 && ` · rows ${firstRowNumber.toLocaleString()}–${lastRowNumber.toLocaleString()}`}
        </span>
        <button disabled={!hasMore} onClick={() => setPage(page + 1)}>
          Next
        </button>
      </div>
    </div>
  );
};

const CheckboxDropdown = ({ label, options, selected, onToggle, onClear }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className='merchants-v2-dropdown'>
      <button className={cn('merchants-v2-chip', { active: selected.length > 0 })} onClick={() => setIsOpen(!isOpen)}>
        {label}
        {selected.length > 0 && <span className='chip-count'>{selected.length}</span>}
        <span className='chip-caret'>{isOpen ? '▴' : '▾'}</span>
      </button>
      {isOpen && (
        <>
          <div className='merchants-v2-dropdown-overlay' onClick={() => setIsOpen(false)} />
          <div className='merchants-v2-dropdown-panel'>
            {options.map(option => (
              <label key={option.value} className='dropdown-option'>
                <input type='checkbox' checked={selected.includes(option.value)} onChange={() => onToggle(option.value)} />
                <span className='dropdown-option-label'>{option.label}</span>
              </label>
            ))}
            {selected.length > 0 && (
              <button className='dropdown-clear' onClick={onClear}>
                Clear {label.toLowerCase()}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/*
  The one place the admin touches S3: the nightly merchants.json / commissions.json uploads per
  network, served as short-lived presigned links so the raw payload can be inspected offline.
*/
const SnapshotDownloadsDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [source, setSource] = useState(SNAPSHOT_SOURCES[0]);
  const [uploads, setUploads] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let isActive = true;
    setUploads(null);
    setError(null);
    getMerchantSnapshotUploads(source)
      .then(result => isActive && setUploads(result.uploads))
      .catch(requestError => isActive && setError(requestError || 'Could not list snapshots.'));
    return () => {
      isActive = false;
    };
  }, [isOpen, source]);

  return (
    <div className='merchants-v2-dropdown'>
      <button className={cn('merchants-v2-chip', { active: isOpen })} onClick={() => setIsOpen(!isOpen)}>
        <FontAwesomeIcon icon={faDownload} /> Network snapshots
        <span className='chip-caret'>{isOpen ? '▴' : '▾'}</span>
      </button>
      {isOpen && (
        <>
          <div className='merchants-v2-dropdown-overlay' onClick={() => setIsOpen(false)} />
          <div className='merchants-v2-dropdown-panel snapshots-panel'>
            <div className='snapshots-sources'>
              {SNAPSHOT_SOURCES.map(snapshotSource => (
                <button
                  key={snapshotSource}
                  className={cn('snapshots-source', { active: source === snapshotSource })}
                  onClick={() => setSource(snapshotSource)}
                >
                  {getSourceLabel(snapshotSource)}
                </button>
              ))}
            </div>
            {error ? (
              <div className='snapshots-note error'>{String(error)}</div>
            ) : !uploads ? (
              <div className='snapshots-note'>Listing {getSourceLabel(source)} uploads...</div>
            ) : !uploads.length ? (
              <div className='snapshots-note'>No {getSourceLabel(source)} snapshots have been uploaded yet.</div>
            ) : (
              uploads.map(upload => (
                <div key={upload.timestampValue} className='snapshots-upload'>
                  <div className='snapshots-upload-date'>{moment(upload.timestamp).format('MMM D, YYYY · h:mm A')}</div>
                  <div className='snapshots-files'>
                    {upload.files.map(file => (
                      <a
                        key={file.key}
                        className='snapshots-file'
                        href={file.downloadUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        title={file.lastModifiedAt ? `Uploaded ${moment(file.lastModifiedAt).format('MMM D, YYYY · h:mm A')}` : undefined}
                      >
                        <FontAwesomeIcon icon={faDownload} />
                        {file.fileName}
                        <span className='snapshots-file-size'>{formatBytes(file.bytes)}</span>
                      </a>
                    ))}
                  </div>
                </div>
              ))
            )}
            <div className='snapshots-note footer'>Links expire after an hour — reopen this menu for fresh ones.</div>
          </div>
        </>
      )}
    </div>
  );
};

const ROW_LOCKS = {
  name: { flag: 'isNameLocked', field: 'name', label: 'name', getValue: row => row.name },
  source: { flag: 'isSourceLocked', field: 'source', label: 'network', getValue: row => row.source },
  payout: { flag: 'isPayoutLocked', field: 'fullPayout', label: 'rate', getValue: row => row.fullPayout },
  note: { flag: 'isDisclaimerLocked', field: 'disclaimer', label: 'note', getValue: row => row.disclaimer || '' }
};

const MerchantRow = ({ row, onBrandClick, onRowChange }) => {
  const [saving, setSaving] = useState(false);
  const networkNameDiffers = row.network_name && row.network_name !== row.name;
  const networkRate = row.commissionDisclaimer?.rate;
  const rateOverridden =
    row.locks?.payout && networkRate !== null && networkRate !== undefined && Math.abs(networkRate - (row.fullPayout ?? 0)) > 0.05;
  // Name, rate and note locks pin the domain's live values, so only the live row offers them.
  const canLockValues = row.isSMSWinner;
  const rateLocked = canLockValues && !!row.locks?.payout;
  const noteLocked = canLockValues && !!row.locks?.note;

  const toggleLock = async (event, lockKey) => {
    event.preventDefault();
    event.stopPropagation();
    if (!row.domain || saving) return;
    const lock = ROW_LOCKS[lockKey];
    const isLocked = !!row.locks?.[lockKey];
    setSaving(true);
    try {
      let testCase;
      if (row.testCase) {
        const updates = isLocked ? { [lock.flag]: false } : { [lock.flag]: true, [lock.field]: lock.getValue(row) };
        await updateMerchantTestCase(row.testCase, updates);
        testCase = { ...row.testCase, ...updates };
      } else {
        testCase = await addMerchantTestCase({
          domain: row.domain,
          name: row.name,
          source: row.source,
          fullPayout: row.fullPayout ?? 0,
          [lock.field]: lock.getValue(row),
          [lock.flag]: true
        });
      }
      const locks = { ...row.locks, [lockKey]: !isLocked };
      onRowChange({ ...row, testCase, locks });
    } catch (saveError) {
      cogoToast.error(String(saveError || 'Could not update the lock. Please try again.'));
    }
    setSaving(false);
  };

  const renderLock = lockKey => {
    if (!row.domain) return null;
    const isLocked = !!row.locks?.[lockKey];
    const { label } = ROW_LOCKS[lockKey];
    return (
      <button
        className={cn('row-lock', { locked: isLocked })}
        disabled={saving}
        title={isLocked ? `The ${label} is locked on ${row.domain} — click to unlock.` : `Lock the current ${label} on ${row.domain}.`}
        onClick={event => toggleLock(event, lockKey)}
      >
        <FontAwesomeIcon icon={isLocked ? faLock : faLockOpen} />
      </button>
    );
  };

  return (
    <Link className={cn('merchants-v2-row', { 'not-live': !row.isSMSWinner })} to={`/merchantsV2/${row.id}`}>
      <div className='col-name'>
        <div className='row-name'>
          <span title={networkNameDiffers ? `${getSourceLabel(row.source)} lists this merchant as "${row.network_name}"` : undefined}>{row.name}</span>
          {canLockValues && renderLock('name')}
          {row.Brand_id && (
            <button
              className='partner-badge-button'
              title='See everything tied to this brand account'
              onClick={event => {
                event.preventDefault();
                onBrandClick(row.Brand_id);
              }}
            >
              <PartnerBadge size={15} />
            </button>
          )}
        </div>
      </div>
      <div className='col-domain'>{row.domain}</div>
      <div className='col-network'>
        <span className={cn('network-tag', getSourceTagClass(row.source))}>{getSourceLabel(row.source)}</span>
        {row.otherSources.length > 0 && (
          <span className='network-tag multi' title={`Also carried by ${row.otherSources.map(getSourceLabel).join(', ')}`}>
            +{row.otherSources.length}
          </span>
        )}
        {renderLock('source')}
      </div>
      <div className='col-rate'>
        <div className='rate-row'>
          <span className={cn('rate-value', { missing: formatMerchantRate(row) === null })}>
            {formatMerchantRate(row) || 'No rate'}
          </span>
          {rateOverridden && (
            <span
              className='overridden-rate'
              title={`The network now sends ${networkRate}% — overridden by the locked ${formatMerchantRate(row)}.`}
            >
              {networkRate}%
            </span>
          )}
          {canLockValues && (formatMerchantRate(row) !== null || rateLocked) && renderLock('payout')}
        </div>
        {row.isClosedProgram && <span className='closed-program-tag'>Closed program</span>}
      </div>
      <div className='col-live'>
        {row.isSMSWinner ? (
          <span className='live-check' title='Creators earn through this merchant today'>
            ✓
          </span>
        ) : (
          <span className='live-dash'>—</span>
        )}
      </div>
      <div className='col-note'>
        <span className='note-text'>{row.disclaimer || <span className='note-empty'>—</span>}</span>
        {canLockValues && (row.disclaimer || noteLocked) && renderLock('note')}
      </div>
      <div className='col-updated'>{row.updatedAt ? moment(row.updatedAt).format('MMM D, YYYY') : '—'}</div>
    </Link>
  );
};

const formatBytes = bytes => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default MerchantsV2;
